import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface AddBotToGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  existingPriorities: number[];
  onSuccess: () => void;
}

interface FormData {
  bot_id: string;
  priority: number;
}

export const AddBotToGroupDialog: React.FC<AddBotToGroupDialogProps> = ({
  open,
  onOpenChange,
  groupId,
  existingPriorities,
  onSuccess,
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      bot_id: '',
      priority: Math.max(...existingPriorities, 0) + 1,
    },
  });

  // Fetch available bots with conditional skip enabled
  const { data: availableBots, isLoading: loadingBots } = useQuery({
    queryKey: ['available-bots-for-group', groupId],
    queryFn: async () => {
      // Get all bots with conditional skip settings
      const { data: botsWithSettings, error: settingsError } = await supabase
        .from('conditional_skip_settings')
        .select('bot_id, bots(id, name)')
        .eq('skip_type', 'FLEXIBLE_PATTERN')
        .eq('enabled', true);

      if (settingsError) throw settingsError;

      // Get bots already in this group
      // @ts-ignore - strategy_group_members table will be created by migration
      const { data: existingMembers, error: membersError } = await supabase
        // @ts-ignore
        .from('strategy_group_members')
        .select('bot_id')
        .eq('group_id', groupId);

      if (membersError) throw membersError;

      const existingBotIds = new Set(existingMembers?.map((m: any) => m.bot_id) || []);

      // Filter out bots already in the group
      return (botsWithSettings || [])
        .filter(s => s.bots && !existingBotIds.has(s.bot_id))
        .map(s => s.bots);
    },
    enabled: open,
  });

  const addBotToGroup = useMutation({
    mutationFn: async (data: FormData) => {
      // Step 1: Insert bot into group
      // @ts-ignore - strategy_group_members table will be created by migration
      const { error } = await supabase
        // @ts-ignore
        .from('strategy_group_members')
        .insert({
          group_id: groupId,
          bot_id: data.bot_id,
          priority: data.priority,
          is_active: true,
        });

      if (error) throw error;
      
      // Step 2: Trigger recalculation of active bot
      const { error: recalcError } = await supabase.functions.invoke(
        'conditional-skip',
        {
          body: { groupId }
        }
      );
      
      if (recalcError) {
        console.error('Failed to recalculate active bot:', recalcError);
        // Don't throw - bot was added successfully
      }
    },
    onSuccess: () => {
      toast.success('Bot added and group recalculated');
      form.reset();
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: any) => {
      if (error.message?.includes('unique')) {
        toast.error('This priority is already taken');
      } else {
        toast.error('Failed to add bot to group');
      }
      console.error(error);
    },
  });

  const onSubmit = (data: FormData) => {
    addBotToGroup.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Bot to Group</DialogTitle>
          <DialogDescription>
            Select a bot and assign a priority (lower number = higher priority)
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="bot_id"
              rules={{ required: 'Bot is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bot</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a bot" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loadingBots ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : !availableBots || availableBots.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground text-center">
                          No available bots (Type 2 Flexible only)
                        </div>
                      ) : (
                        availableBots.map((bot: any) => (
                          <SelectItem key={bot.id} value={bot.id}>
                            {bot.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Only Type 2 Flexible bots can be added to groups
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              rules={{
                required: 'Priority is required',
                min: { value: 1, message: 'Priority must be at least 1' },
                validate: (value) => {
                  if (existingPriorities.includes(value)) {
                    return 'This priority is already taken';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    Lower number = higher priority (1 is highest)
                    {existingPriorities.length > 0 && (
                      <span className="block mt-1">
                        Existing: {existingPriorities.sort((a, b) => a - b).join(', ')}
                      </span>
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={addBotToGroup.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addBotToGroup.isPending || !availableBots || availableBots.length === 0}
              >
                {addBotToGroup.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Add Bot
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
